/**
 * ProjectComposeEmail (2026-02-17)
 * ----------------------------------
 * In-app compose modal launched from a workflow step's "Compose Email"
 * Quick Action. Replaces the old `mailto:` hand-off.
 *
 * Flow:
 *   1. On open, fetch the project's placeholder variables and the
 *      Library email templates for `category` (falls back to all).
 *   2. Pick the closest-match template by default (name/category
 *      contains "welcome"/"onboarding" first, else the first template).
 *   3. On template select, call POST /api/project-email/render which
 *      substitutes {{client_name}}, {{project_name}}, {{project_address}},
 *      {{company_name}}, {{client_email}} server-side and returns
 *      editable subject + body_html.
 *   4. User can edit subject/body/to_email inline.
 *   5. Send calls POST /api/project-email/send. On success, offer
 *      "Mark this step complete?" via the onSent callback.
 *
 * Everything is tenant-scoped by the backend — this component never
 * bypasses those checks.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader2, AlertTriangle, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

// Category hints for BEST-GUESS preselection when no remembered
// default exists. The unified list is always fetched in full — this
// only steers the initial highlight.
const STEP_CATEGORY_HINTS = [
  { match: /welcome|onboarding|greeting/i, category: 'onboarding' },
  { match: /change.order/i, category: 'change_order' },
  { match: /milestone/i, category: 'milestone' },
  { match: /selection|approval/i, category: 'approval' },
  { match: /rfi/i, category: 'rfi' },
];

function inferCategory(stepName) {
  const s = (stepName || '').toString();
  for (const h of STEP_CATEGORY_HINTS) if (h.match.test(s)) return h.category;
  return 'general';
}

/**
 * Pick the best template for a step. Priority:
 *  1. Remembered tenant default for (phase, step) — if it still resolves.
 *  2. Name/phase match — for "Send Welcome Email" this lands on
 *     "Welcome & Greeting" (matches "welcome"/"greeting").
 *  3. First template in the list.
 */
function pickBestTemplate(list, { stepName, phaseName, remembered, defaultTemplateKey }) {
  if (!Array.isArray(list) || list.length === 0) return null;
  if (remembered?.template_id) {
    const hit = list.find(t => t.id === remembered.template_id);
    if (hit) return hit;
  }
  // 2026-02-17 — Deterministic hint from the phase-template step:
  // `default_email_template_key` (e.g. "welcome_greeting") maps to a
  // template id that ends with `:{key}` (journey ids are prefixed
  // with `{company_id}:`) OR equals the key directly. Use it before
  // any name-scoring so brand-new tenants land on the right template.
  if (defaultTemplateKey) {
    const suffix = `:${defaultTemplateKey}`;
    const keyHit = list.find(
      t => t.id === defaultTemplateKey || (t.id || '').endsWith(suffix),
    );
    if (keyHit) return keyHit;
  }
  const nameHay = `${stepName || ''} ${phaseName || ''}`;
  // Score each template against the step. Higher = closer.
  const scoreOf = (t) => {
    const hay = `${t.name || ''} ${t.category || ''} ${t.phase || ''} ${t.trigger_step || ''}`;
    let s = 0;
    if (/welcome|greeting|onboarding/i.test(nameHay) &&
        /welcome|greeting/i.test(hay)) s += 100;
    if (t.phase && phaseName && t.phase.toLowerCase() === phaseName.toLowerCase()) s += 30;
    // Category → step-name keyword loose match.
    const cat = (t.category || '').toLowerCase();
    if (cat && new RegExp(cat, 'i').test(nameHay)) s += 10;
    // Direct trigger_step match wins outright.
    if (t.trigger_step && stepName &&
        t.trigger_step.toLowerCase().includes((stepName || '').toLowerCase().replace(/^send\s+/i, ''))) s += 200;
    return s;
  };
  const ranked = [...list].sort((a, b) => scoreOf(b) - scoreOf(a));
  const top = ranked[0];
  return (scoreOf(top) > 0) ? top : list[0];
}

export function ProjectComposeEmail({
  open,
  onOpenChange,
  projectId,
  projectName,
  stepId,
  stepName,
  stepPhase,
  defaultTemplateKey,
  onSent,
}) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);          // unified list
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [variables, setVariables] = useState(null);
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [rendering, setRendering] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sentSuccessId, setSentSuccessId] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const inferredCategory = useMemo(() => inferCategory(stepName), [stepName]);
  const hasClientEmail = Boolean(variables?.client_email);

  // Available categories = distinct categories in the unified list, +
  // "all". The "all" default lets the user see everything; the initial
  // filter is set to the inferred category if any templates match.
  const availableCategories = useMemo(() => {
    const set = new Set(templates.map(t => (t.category || '').toLowerCase()).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [templates]);

  const visibleTemplates = useMemo(() => {
    if (categoryFilter === 'all') return templates;
    return templates.filter(t => (t.category || '').toLowerCase() === categoryFilter);
  }, [templates, categoryFilter]);

  // Reset all state whenever the modal (re)opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setSendError('');
      setSentSuccessId('');
      setSubject('');
      setBodyHtml('');
      setToEmail('');
      setSelectedTemplateId('');
      setCategoryFilter('all');
      try {
        // 1) Project variables (tenant-scoped server-side).
        const varsRes = await axios.get(
          `${API}/api/project-email/variables/${projectId}`
        );
        if (cancelled) return;
        const vars = varsRes.data || {};
        setVariables(vars);
        setToEmail(vars.client_email || '');

        // 2) Unified template list — all 29 (or however many) rows,
        // deduped and source-tagged by the backend.
        let list = [];
        try {
          const unifiedRes = await axios.get(`${API}/api/email-templates/unified`);
          const payload = unifiedRes.data;
          list = Array.isArray(payload?.templates) ? payload.templates
              : (Array.isArray(payload) ? payload : []);
        } catch {
          list = [];
        }
        if (cancelled) return;
        setTemplates(list);

        // 3) Remembered default for this step (tenant-scoped).
        let remembered = null;
        if (stepName) {
          try {
            const memRes = await axios.get(`${API}/api/project-email/step-default`, {
              params: { phase: stepPhase || '', step: stepName },
            });
            remembered = memRes.data || null;
          } catch { /* silent — no memory yet */ }
        }

        // 4) Preselect: remembered → step hint key → best-guess → first.
        const pick = pickBestTemplate(list, {
          stepName, phaseName: stepPhase, remembered, defaultTemplateKey,
        });
        if (pick) {
          setSelectedTemplateId(pick.id);
          // Set category filter to the picked template's category by
          // default (falls back to inferred, then 'all'). The user can
          // switch to 'all' at any time.
          const initialCat = (pick.category || inferredCategory || 'all').toLowerCase();
          if (list.some(t => (t.category || '').toLowerCase() === initialCat)) {
            setCategoryFilter(initialCat);
          }
          await renderTemplate(pick.id);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err.response?.data?.detail || 'Failed to load email composer'
          );
          onOpenChange(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId, stepName, stepPhase]);

  const renderTemplate = async (templateId) => {
    if (!templateId) return;
    setRendering(true);
    setSendError('');
    try {
      const res = await axios.post(`${API}/api/project-email/render`, {
        project_id: projectId,
        template_id: templateId,
      });
      setSubject(res.data?.subject || '');
      setBodyHtml(res.data?.body_html || '');
      if (res.data?.variables) setVariables(res.data.variables);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load template');
    } finally {
      setRendering(false);
    }
  };

  const handleTemplateChange = async (id) => {
    setSelectedTemplateId(id);
    await renderTemplate(id);
  };

  const selectedTemplate = useMemo(
    () => templates.find(t => t.id === selectedTemplateId),
    [templates, selectedTemplateId],
  );

  const handleSend = async () => {
    setSendError('');
    if (!toEmail.trim()) { setSendError('Recipient email is required.'); return; }
    if (!subject.trim()) { setSendError('Subject is required.'); return; }
    if (!bodyHtml.trim()) { setSendError('Body is required.'); return; }

    setSending(true);
    try {
      const res = await axios.post(`${API}/api/project-email/send`, {
        project_id: projectId,
        step_id: stepId || null,
        template_id: selectedTemplateId || null,
        to_email: toEmail.trim(),
        subject,
        body_html: bodyHtml,
      });
      const sentId = res.data?.sent_email_id || '';
      setSentSuccessId(sentId);
      toast.success(`Email sent to ${toEmail}`);
      // Remember this template as the default for (phase, step). Best
      // effort — the send already succeeded, so a memory-write failure
      // shouldn't surface an error to the user.
      if (stepName && selectedTemplateId) {
        try {
          await axios.post(`${API}/api/project-email/step-default`, {
            phase: stepPhase || '',
            step: stepName,
            template_id: selectedTemplateId,
            template_source: selectedTemplate?.source || null,
            template_name: selectedTemplate?.name || null,
          });
        } catch { /* non-fatal */ }
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send email.';
      setSendError(detail);
    } finally {
      setSending(false);
    }
  };

  const handleMarkComplete = async () => {
    if (onSent && stepId) {
      try { await onSent({ stepId, markComplete: true, sentEmailId: sentSuccessId }); }
      catch { /* parent handles its own error */ }
    }
    onOpenChange(false);
  };

  const handleDismissAfterSend = () => {
    if (onSent) {
      try { onSent({ stepId, markComplete: false, sentEmailId: sentSuccessId }); }
      catch { /* noop */ }
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="project-compose-email-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-[#BC5D41]" />
            Compose Email{stepName ? ` — ${stepName}` : ''}
          </DialogTitle>
          <DialogDescription>
            {projectName ? `Project: ${projectName}. ` : ''}
            Choose a template, personalize the content, and send.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex items-center justify-center text-sm text-slate-500">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Loading templates…
          </div>
        ) : sentSuccessId ? (
          <div className="py-6 space-y-4">
            <div className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
              Email sent to <span className="font-medium">{toEmail}</span>.
            </div>
            {stepId ? (
              <div className="space-y-2">
                <p className="text-sm">Would you like to mark this step complete?</p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleMarkComplete}
                    className="bg-[#70170F] hover:bg-[#8B1E14] text-white"
                    data-testid="project-compose-mark-complete-btn"
                  >
                    Mark step complete
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDismissAfterSend}
                    data-testid="project-compose-keep-open-btn"
                  >
                    Not yet
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex justify-end">
                <Button onClick={handleDismissAfterSend}>Close</Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Template picker */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <Label htmlFor="pce-template" className="mb-0">Template</Label>
                {availableCategories.length > 2 && (
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger
                      className="h-7 text-xs w-40"
                      data-testid="project-compose-category-filter"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCategories.map(c => (
                        <SelectItem key={c} value={c}>
                          {c === 'all' ? 'All categories' : c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Select value={selectedTemplateId} onValueChange={handleTemplateChange}>
                <SelectTrigger id="pce-template" data-testid="project-compose-template-select">
                  <SelectValue placeholder="Choose a template…" />
                </SelectTrigger>
                <SelectContent>
                  {visibleTemplates.length === 0 && (
                    <SelectItem value="__none__" disabled>No templates in this filter</SelectItem>
                  )}
                  {visibleTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}{t.category ? ` · ${t.category}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {rendering && (
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Auto-filling placeholders…
                </div>
              )}
            </div>

            {/* Recipient */}
            <div>
              <Label htmlFor="pce-to">Recipient</Label>
              <Input
                id="pce-to"
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="recipient@example.com"
                data-testid="project-compose-to-input"
              />
              {!hasClientEmail && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1 flex items-start gap-1">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    This project has no client email on file. Type a recipient
                    address, then add it to the project when you have a moment.
                  </span>
                </div>
              )}
            </div>

            {/* Subject */}
            <div>
              <Label htmlFor="pce-subject">Subject</Label>
              <Input
                id="pce-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject line"
                data-testid="project-compose-subject-input"
              />
            </div>

            {/* Body */}
            <div>
              <Label htmlFor="pce-body">Body (HTML)</Label>
              <Textarea
                id="pce-body"
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                rows={12}
                className="font-mono text-xs"
                placeholder="Email body — HTML allowed. Placeholders are already filled."
                data-testid="project-compose-body-input"
              />
            </div>

            {/* Sending identity notice */}
            <div className="text-xs text-slate-500 flex items-start gap-1">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Sending as{' '}
                <span className="font-medium">
                  {variables?.company_name || 'your company'}
                </span>{' '}
                via KEY-MADE — replies go to your email.
              </span>
            </div>

            {sendError && (
              <div
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2"
                data-testid="project-compose-error"
              >
                {sendError}
              </div>
            )}
          </div>
        )}

        {!sentSuccessId && !loading && (
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={sending}
              data-testid="project-compose-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || rendering}
              className="bg-[#70170F] hover:bg-[#8B1E14] text-white"
              data-testid="project-compose-send-btn"
            >
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : <><Send className="w-4 h-4 mr-2" />Send email</>}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ProjectComposeEmail;
